// Globals
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import debounce from 'lodash.debounce';
import { AutoComplete } from 'antd';
import { memo, useCallback, useMemo, useState } from 'react';

// Modules
import { useGetLocales } from '@/modules/Locales';

// Models
import { IMultiLanguageAddress } from '@/models/place.model';
import { SupportedLocales } from '@/models/localization.model';

// Helpers
import { useAutocompleteSuggestions } from '../../hooks/useAutocompleteSuggestions';

type TMultiLangAutocompleteProps = {
  onChange?: (place: IMultiLanguageAddress | null) => void;
  value?: IMultiLanguageAddress;
  cityCenter?: { latitude?: string; longitude?: string; radius?: number };
  placeholder?: string;
  withCityName?: boolean;
};

export const MultiLangAutocomplete = ({
  onChange,
  value,
  placeholder = '',
  withCityName = false,
  cityCenter,
}: TMultiLangAutocompleteProps) => {
  const places = useMapsLibrary('places');
  const locales = useGetLocales() ?? [];
  const mainLang = useMemo(() => locales?.[0] ?? SupportedLocales.UK, [locales]);
  const [inputValue, setInputValue] = useState<string>(() => value?.locale[mainLang]?.name ?? '');
  const [debouncedValue, setDebouncedValue] = useState('');

  const { suggestions, resetSession } = useAutocompleteSuggestions(
    debouncedValue,
    {
      language: mainLang,
      includedRegionCodes: ['ua'],
    },
    cityCenter,
  );

  const debouncedSetValue = useMemo(() => debounce((val: string) => setDebouncedValue(val), 500), []);

  const handleChange = (query: string) => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
      setInputValue('');
      return onChange?.(null);
    }
    debouncedSetValue(query);
    setInputValue(query);
  };

  const handleSuggestionClick = useCallback(
    async (placeId: string) => {
      const suggestion = suggestions.find(({ placePrediction }) => placePrediction?.placeId === placeId);
      if (!places || !suggestion?.placePrediction) return;

      const place = suggestion.placePrediction.toPlace();

      await place.fetchFields({ fields: ['location', 'formattedAddress', 'addressComponents'] });

      const addressComponents = place.addressComponents;
      const streetNumber = addressComponents?.find((c) => c.types.includes('street_number'))?.longText;
      const route = addressComponents?.find((c) => c.types.includes('route'))?.longText;
      const city = addressComponents?.find((c) => c.types.includes('locality'))?.longText;
      const shortAddress = `${withCityName ? `${city}, ` : ''}${route}${
        streetNumber ? `, ${streetNumber}` : ''
      }`.trim();

      const lat = place.location?.lat() ?? 0;
      const lng = place.location?.lng() ?? 0;

      const translations: Record<string, { name: string }> = {
        [mainLang]: { name: shortAddress ?? '' },
      };

      const fetchInOtherLangs = async (lang: string) => {
        const element = document.createElement('div');
        const service = new window.google.maps.places.PlacesService(element);

        return new Promise<{ lang: string; name: string } | null>(async (resolve) => {
          service.findPlaceFromQuery(
            {
              query: place.formattedAddress ?? '',
              locationBias: new window.google.maps.LatLng(lat, lng),
              fields: ['name', 'formatted_address'],
              language: lang,
            },
            async (results, status) => {
              if (status === window.google.maps.places.PlacesServiceStatus.OK && results?.[0]) {
                resolve({ lang, name: results[0].name || results[0].formatted_address || '' });
              } else {
                resolve(null);
              }
              element.remove();
            },
          );
        });
      };

      const langRequests = locales.filter((lang) => lang !== mainLang).map((lang) => fetchInOtherLangs(lang));

      const results = await Promise.all(langRequests);

      for (const res of results) {
        if (res) {
          translations[res.lang] = { name: res.name };
        }
      }

      resetSession();

      setInputValue(suggestion.placePrediction?.text.text ?? place.formattedAddress ?? '');

      onChange?.({
        latitude: lat,
        longitude: lng,
        locale: translations,
        place_id: place.id,
      });
    },
    [suggestions, resetSession, onChange, places, mainLang, locales],
  );

  return (
    <AutoComplete
      placeholder={placeholder}
      showSearch
      onSearch={handleChange}
      onSelect={handleSuggestionClick}
      options={suggestions.map((suggestion) => ({
        value: suggestion.placePrediction?.placeId,
        label: suggestion.placePrediction?.text.text ?? '',
      }))}
      value={inputValue || value?.locale[mainLang]?.name || ''}
    />
  );
};

export default memo(MultiLangAutocomplete);
